<template lang="pug">
.mis-reportes
  .rp-head
    div
      h1.rp-title Dudas del tratamiento?
      p.rp-sub ¿Tenés una duda o algo no va bien con tu tratamiento? Contanos y tu equipo médico te responde.
    button.rp-new(@click="openModal")
      span(v-html="icon('plus')")
      | Nueva consulta

  .rp-loading(v-if="loading")
    .rp-spinner
    span Cargando…

  template(v-else)
    h2.rp-section Mis consultas
    .rp-empty(v-if="!reports.length")
      span(v-html="icon('alert')")
      p Todavía no hiciste ninguna consulta.
    .rp-list(v-else)
      .rp-card(v-for="r in reports" :key="r._id")
        .rp-card__ico(:class="statusOf(r).cls" v-html="icon('alert')")
        .rp-card__body
          .rp-card__name {{ reportName(r) }}
          .rp-card__obs(v-if="r.observation") {{ r.observation }}
          .rp-card__meta
            span.rp-when {{ whenLabel(r) }}
            span.rp-status(:class="statusOf(r).cls") {{ statusOf(r).label }}

  //- Modal crear
  .rp-scrim(v-show="modalOpen" @click="closeModal")
  .rp-modal(v-show="modalOpen")
    .rp-modal__head
      h3 Nueva consulta
      button.rp-modal__close(@click="closeModal") ✕
    .rp-modal__body
      .rp-field(v-if="templates.length")
        label Tipo de problema
        select(v-model="form.templateId")
          option(value="") Otro
          option(v-for="t in templates" :key="t._id" :value="t._id") {{ t.name }}
      .rp-field
        label Contanos qué pasó
        textarea(
          v-model="form.observation"
          rows="4"
          placeholder="Ej: tengo náuseas con la medicación, no consigo el medicamento, etc."
        )
      .rp-field
        label Foto (opcional)
        .rp-file
          button.rp-file__btn(type="button" @click="pickFile")
            span(v-html="icon('clip')")
            | {{ form.file ? 'Cambiar foto' : 'Adjuntar foto' }}
          span.rp-file__name(v-if="form.file") {{ form.file.name }}
          input(ref="fileInput" type="file" accept="image/*" style="display:none" @change="onFile")
    .rp-modal__foot
      button.rp-btn.rp-btn--ghost(@click="closeModal") Cancelar
      button.rp-btn.rp-btn--teal(:disabled="!canSubmit || submitting" @click="submit")
        | {{ submitting ? 'Enviando…' : 'Enviar consulta' }}
</template>

<script>
import reportService from "@/services/report";

const ICONS = {
  plus: '<path d="M12 5v14M5 12h14"/>',
  alert:
    '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>',
  clip: '<path d="M21.4 11.05 12.25 20.2a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.41l8.49-8.49"/>',
};

/**
 * @component Reportes
 * @description Portal del paciente — "Reportar un problema". Lista los reportes
 *   del paciente y permite crear uno nuevo (tipo/plantilla + descripción + foto
 *   opcional) que el médico ve en su bandeja. Reusa el tipo de tarea `reports`.
 * @example <Reportes />
 */
export default {
  name: "Reportes",
  data() {
    return {
      loading: true,
      submitting: false,
      reports: [],
      templates: [],
      modalOpen: false,
      form: { templateId: "", observation: "", file: null },
    };
  },
  computed: {
    canSubmit() {
      return this.form.observation.trim().length > 0;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    icon(name) {
      return (
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
        (ICONS[name] || "") +
        "</svg>"
      );
    },
    async fetch() {
      this.loading = true;
      try {
        const [reports, templates] = await Promise.all([
          reportService.getMyReports(),
          reportService.getTemplates(),
        ]);
        this.reports = reports;
        this.templates = templates;
      } finally {
        this.loading = false;
      }
    },
    reportName(r) {
      return r && r.name ? r.name : "Consulta";
    },
    /**
     * @description Estado visual del reporte: si el médico ya lo verificó,
     *              "Revisado"; si no, "Enviado".
     * @param {Object} r
     * @returns {{label:string, cls:string}}
     */
    statusOf(r) {
      if (r && r.verifiedAt) {
        return { label: "Respondida por tu médico", cls: "is-reviewed" };
      }
      return { label: "Enviada · en revisión", cls: "is-pending" };
    },
    whenLabel(r) {
      const d = r && r.createdAt ? new Date(r.createdAt) : null;
      if (!d || Number.isNaN(d.getTime())) return "";
      const diff = Date.now() - d.getTime();
      const day = 86400000;
      if (diff < 3600000) return "hace un rato";
      if (diff < day) return "hoy";
      if (diff < 2 * day) return "ayer";
      if (diff < 7 * day) return `hace ${Math.floor(diff / day)} días`;
      return d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
    },
    openModal() {
      this.form = { templateId: "", observation: "", file: null };
      this.modalOpen = true;
    },
    closeModal() {
      this.modalOpen = false;
    },
    pickFile() {
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    onFile(e) {
      const f = e.target.files && e.target.files[0];
      if (f) this.form.file = f;
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      try {
        const template = this.templates.find(
          (t) => t._id === this.form.templateId
        );
        const name = template ? template.name : "Otro problema";
        const created = await reportService.create({
          name,
          observation: this.form.observation.trim(),
          treatmentTask: this.form.templateId || undefined,
        });
        if (this.form.file && created && created._id) {
          try {
            await reportService.uploadFile(created._id, this.form.file);
          } catch (e) {
            // La foto es opcional: si falla el upload, el reporte igual quedó.
            this.$message &&
              this.$message.warning(
                "La consulta se envió, pero no pudimos adjuntar la foto."
              );
          }
        }
        this.modalOpen = false;
        this.$message &&
          this.$message.success("Enviamos tu consulta a tu equipo médico.");
        await this.fetch();
      } catch (err) {
        const status = err && err.response ? err.response.status : null;
        this.$message &&
          this.$message.error(
            status === 400
              ? "No pudimos registrar la consulta (necesitás un tratamiento activo). Consultá con tu médico."
              : "No pudimos enviar la consulta. Intentá de nuevo."
          );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: #1b2233;
$muted: #697392;
$faint: #98a2bd;
$surface: #fff;
$surface-2: #f6f8fc;
$border: #e6eaf3;
$border-strong: #d5dcec;
$teal: #12a594;
$teal-700: #0e8578;
$navy: #1b2b63;

.mis-reportes {
  font-family: "Plus Jakarta Sans", system-ui, -apple-system, Segoe UI, Roboto,
    sans-serif;
  color: $ink;
  padding: 4px 2px 40px;
}
.rp-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}
.rp-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  color: #0f172a;
}
.rp-sub {
  margin: 4px 0 0;
  color: $muted;
  font-size: 13px;
  max-width: 52ch;
}
.rp-new {
  flex: none;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: #fff;
  background: $teal;
  border: 1.5px solid $teal;
  border-radius: 9px;
  padding: 9px 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: 0.15s;
}
.rp-new:hover {
  background: $teal-700;
  border-color: $teal-700;
}
.rp-new ::v-deep svg {
  width: 15px;
  height: 15px;
}

.rp-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: $muted;
  font-size: 13.5px;
  padding: 30px 4px;
}
.rp-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid $border;
  border-top-color: $teal;
  border-radius: 50%;
  animation: rp-spin 0.8s linear infinite;
}
@keyframes rp-spin {
  to {
    transform: rotate(360deg);
  }
}

.rp-section {
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $muted;
  margin: 0 2px 12px;
}
.rp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: $faint;
  padding: 40px 12px;
  text-align: center;
}
.rp-empty ::v-deep svg {
  width: 30px;
  height: 30px;
}
.rp-empty p {
  margin: 0;
  font-size: 13px;
  font-style: italic;
}

.rp-list {
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.rp-card {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  background: $surface;
  border: 1px solid $border;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(27, 34, 51, 0.05),
    0 8px 22px rgba(27, 34, 51, 0.05);
}
.rp-card__ico {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: #faf0dd;
  color: #c67c12;
}
.rp-card__ico.is-reviewed {
  background: #e1f4ec;
  color: #0f9d6b;
}
.rp-card__ico ::v-deep svg {
  width: 18px;
  height: 18px;
}
.rp-card__body {
  flex: 1;
  min-width: 0;
}
.rp-card__name {
  font-weight: 600;
  font-size: 13.5px;
  color: #0f172a;
}
.rp-card__obs {
  color: $muted;
  font-size: 12.5px;
  margin-top: 3px;
  line-height: 1.5;
}
.rp-card__meta {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 7px;
  flex-wrap: wrap;
}
.rp-when {
  font-size: 11.5px;
  color: $faint;
  font-variant-numeric: tabular-nums;
}
.rp-status {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.rp-status.is-pending {
  background: #faf0dd;
  color: #c67c12;
}
.rp-status.is-reviewed {
  background: #e1f4ec;
  color: #0f9d6b;
}

/* modal */
.rp-scrim {
  position: fixed;
  inset: 0;
  background: rgba(16, 22, 40, 0.44);
  z-index: 2000;
}
.rp-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(460px, 94vw);
  max-height: 90vh;
  background: $surface;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(20, 28, 54, 0.28);
  z-index: 2001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rp-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid $border;
}
.rp-modal__head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.rp-modal__close {
  background: $surface-2;
  border: 1px solid $border;
  border-radius: 9px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  color: $muted;
}
.rp-modal__body {
  padding: 18px 20px;
  overflow-y: auto;
}
.rp-field {
  margin-bottom: 16px;
}
.rp-field label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $muted;
  margin-bottom: 7px;
}
.rp-field select,
.rp-field textarea {
  width: 100%;
  font: inherit;
  font-size: 13.5px;
  color: $ink;
  padding: 10px 12px;
  border: 1.5px solid $border-strong;
  border-radius: 10px;
  background: $surface-2;
  resize: vertical;
}
.rp-field select:focus,
.rp-field textarea:focus {
  outline: none;
  border-color: $teal;
  background: $surface;
}
.rp-file {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.rp-file__btn {
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: $muted;
  background: transparent;
  border: 1.5px solid $border-strong;
  border-radius: 9px;
  padding: 8px 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.rp-file__btn:hover {
  border-color: $teal;
  color: $teal;
}
.rp-file__btn ::v-deep svg {
  width: 14px;
  height: 14px;
}
.rp-file__name {
  font-size: 12px;
  color: $muted;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}
.rp-modal__foot {
  padding: 14px 20px;
  border-top: 1px solid $border;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.rp-btn {
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 9px;
  padding: 10px 18px;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: 0.15s;
}
.rp-btn[disabled] {
  opacity: 0.55;
  cursor: default;
}
.rp-btn--teal {
  background: $teal;
  color: #fff;
  border-color: $teal;
}
.rp-btn--teal:hover {
  background: $teal-700;
  border-color: $teal-700;
}
.rp-btn--ghost {
  background: transparent;
  color: $muted;
  border-color: $border-strong;
}
.rp-btn--ghost:hover {
  border-color: $navy;
  color: $navy;
}

@media (max-width: 560px) {
  .rp-new {
    width: 100%;
    justify-content: center;
  }
}
</style>
